import { useCallback, useMemo } from 'react'
import useVariableTree from '../../hooks/useVariableTree'
import { getNodeID } from '../../utils/nodeTypes'
import { createFilterFromSelectedVariables } from '../../utils/filter'
import type { GenNode } from '../../types/carbon/models/GenNode'
import { parseFilter, type Condition } from './builderModel'

// Data for the construct builder: the job's variables and their categories.
// The builder shows no respondent counts. Counting means a Carbon preview,
// which reads every case in the job (4-18 seconds per call on Sky's Prius
// job), so counts are seen in a table once the construct is created.

export interface Category {
  code: string
  label: string
}

export interface VariableInfo {
  name: string
  label: string
  node: GenNode
}

export const useVariableCatalog = (customer: string, job: string) => {
  const {
    nodes, codes, loadCodesForVariable, isLoading,
    nodeMap, selectedVariables, getCheckboxState, onNodeCheckedToggle, resetChecked,
  } = useVariableTree({ customer, job, loadCodes: true })

  // What is ticked in the tree, as conditions (Codes). The same
  // checkbox state and the same conversion as the spec editor's filter:
  // a ticked variable, or all of its answers, is var(*) ("any answer"); a
  // variable with no answers (a plain number) is var(*) too ("answered it");
  // otherwise its ticked answers. One condition per variable, joined by and.
  const selection = useMemo((): Condition[] => {
    if (selectedVariables.length === 0) return []
    return parseFilter(createFilterFromSelectedVariables(selectedVariables, nodeMap, true)) || []
  }, [selectedVariables, nodeMap])

  const variables = useMemo(() => {
    const flat: VariableInfo[] = []
    const walk = (list: GenNode[]) => {
      for (const node of list) {
        if (node.type === 'Variable' && node.value1) {
          // The question text is the label people know a variable by
          // (the tree shows it as "gender - Gender").
          const question = node.meta?.find(m => m.key === 'question')?.value
          flat.push({ name: node.value1, label: question || node.value1, node })
        }
        if (node.children && node.children.length > 0) walk(node.children)
      }
    }
    walk(nodes || [])
    return flat
  }, [nodes])

  const byName = useMemo(() => new Map(variables.map(v => [v.name, v])), [variables])

  const categoriesOf = useCallback((name: string): Category[] | undefined => {
    const v = byName.get(name)
    if (!v) return undefined
    const loaded = codes[getNodeID(v.node)]
    if (!loaded) return undefined
    return loaded
      .filter(c => c.value1 !== undefined && c.value1 !== '')
      .map(c => ({ code: String(c.value1), label: c.value2 || String(c.value1) }))
  }, [byName, codes])

  const labelOf = useCallback((name: string, code: string) =>
    categoriesOf(name)?.find(c => c.code === code)?.label || code, [categoriesOf])

  // ensureVariable loads a variable's categories (Carbon varCodes, which
  // reads no case data). It resolves to the categories, so a caller can act
  // once they're known.
  const ensureVariable = useCallback(async (name: string): Promise<Category[]> => {
    const v = byName.get(name)
    if (!v) return []
    const loaded = (await loadCodesForVariable(v.node)) || []
    return loaded
      .filter(c => c.value1 !== undefined && c.value1 !== '')
      .map(c => ({ code: String(c.value1), label: c.value2 || String(c.value1) }))
  }, [byName, loadCodesForVariable])

  return {
    nodes: nodes || [], variables, byName, categoriesOf, labelOf, ensureVariable, isLoading,
    selection, getCheckboxState, onNodeCheckedToggle, clearSelection: resetChecked,
  }
}
